'use client'

import { useState, useRef } from 'react'

export default function VoteButtons({ 
  postId, 
  initialScore, 
  userInitialVote 
}: { 
  postId: string, 
  initialScore: number, 
  userInitialVote?: number 
}) {
  const [score, setScore] = useState(initialScore)
  const [currentVote, setCurrentVote] = useState<number | undefined>(userInitialVote)
  
  const [loading, setLoading] = useState(false)
  // 1. THE INSTANT LOCK: This blocks rapid-fire clicks at the exact millisecond they happen
  const isProcessing = useRef(false)

  const handleVote = async (value: number) => {
    // 2. Check the physical ref instead of the React state
    if (isProcessing.current) return
    
    // 3. Immediately lock the ref, then update the visual state
    isProcessing.current = true
    setLoading(true)

    try {
      const res = await fetch(`/api/posts/${postId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to vote')

      setScore(data.score)
      // Toggle vote state (if clicking the same vote type, it removes it)
      setCurrentVote(currentVote === value ? undefined : value)
    } catch (err) {
      console.error(err)
    } finally {
      // 4. Unlock everything once the server is completely finished
      isProcessing.current = false
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center space-x-1 bg-slate-800/60 border border-slate-700/50 rounded-lg px-2 py-1 text-xs">
      {/* Upvote Button */}
      <button 
        onClick={() => handleVote(1)}
        disabled={loading}
        className={`p-1 rounded transition-colors ${
          loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700'
        } ${currentVote === 1 ? 'text-indigo-400 font-bold' : 'text-slate-400'}`}
        title="Upvote"
      >
        ▲
      </button>

      {/* Score */}
      <span className="font-semibold px-1 text-slate-200">{score}</span>

      {/* Downvote Button */}
      <button 
        onClick={() => handleVote(-1)}
        disabled={loading}
        className={`p-1 rounded transition-colors ${
          loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700'
        } ${currentVote === -1 ? 'text-rose-400 font-bold' : 'text-slate-400'}`}
        title="Downvote"
      >
        ▼
      </button>
    </div>
  )
}